import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import {
  ROUTE_ABOUT,
  ROUTE_ACCOUNT_DETAILS,
  ROUTE_DAO,
  ROUTE_HOME,
  ROUTE_INVITE,
  ROUTE_SALE,
  ROUTE_TX_QUEUE,
  SETTINGS,
  ROUTE_CREATE_TOKEN,
} from "@/utils/constants";
import i18n from "@/plugins/i18n";

import HomeView from "@/views/HomeView.vue";
import TransactionQueue from "@/views/TransactionQueue.vue";

const ROUTER_CONTROLLED_META = "data-vue-router-controlled";

/**
 * Return translated key or empty to avoid displaying the keys in the browser titles.
 */
function tOrEmpty(key: string) {
  return () => {
    const result = i18n.global.t(key);
    return result === key ? "" : result;
  };
}

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: ROUTE_HOME,
    component: HomeView,
    meta: {
      title: tOrEmpty("views.home.metaTitle"),
      metaTags: [
        {
          name: "description",
          content: tOrEmpty("views.home.metaDescription"),
        },
      ],
    },
  },
  {
    path: "/tx-queue/:id",
    name: ROUTE_TX_QUEUE,
    component: TransactionQueue,
  },
  {
    path: "/sale/:contractAddress",
    name: ROUTE_SALE,
    component: () =>
      import(
        /* webpackChunkName: "token-sale-details" */ "../views/TokenSaleDetails.vue"
      ),
    meta: {
      title: tOrEmpty("views.tokenSale.metaTitle"),
    },
  },
  {
    path: "/sale/:saleAddress/dao",
    name: ROUTE_DAO,
    component: () =>
      import(
        /* webpackChunkName: "treasury-details" */ "../views/Dao/TreasuryView.vue"
      ),
  },
  {
    path: "/sale/:saleAddress/dao/create",
    name: "create-vote",
    component: () =>
      import(
        /* webpackChunkName: "create-vote" */ "../views/Dao/CreateVoteView.vue"
      ),
  },
  {
    path: "/sale/:saleAddress/dao/:voteId/:voteAddress",
    name: "vote-details",
    component: () =>
      import(
        /* webpackChunkName: "vote-details" */ "../views/Dao/VoteView.vue"
      ),
  },
  {
    path: "/create-token",
    meta: {
      title: tOrEmpty("views.createToken.metaTitle"),
    },
    name: ROUTE_CREATE_TOKEN,
    component: () =>
      import(
        /* webpackChunkName: "select-collection" */ "../views/CreateTokenView.vue"
      ),
  },
  // {
  //   path: "/create-token/:collection",
  //   name: ROUTE_CREATE_TOKEN,
  //   component: () =>
  //     import(
  //       /* webpackChunkName: "create-token" */ "../views/CreateTokenView.vue"
  //     ),
  // },
  {
    path: "/invite",
    name: ROUTE_INVITE,
    component: () =>
      import(/* webpackChunkName: "invite" */ "../views/InviteView.vue"),
    meta: {
      title: tOrEmpty("views.invite.metaTitle"),
    },
  },
  {
    path: "/about",
    name: ROUTE_ABOUT,
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/AboutView.vue"),
    meta: {
      title: tOrEmpty("views.about.metaTitle"),
    },
  },
  {
    path: "/accounts/:address",
    name: ROUTE_ACCOUNT_DETAILS,
    component: () =>
      import(
        /* webpackChunkName: "account-details" */ "../views/Accounts/AccountDetails.vue"
      ),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to) => {
  const nearestWithTitle = [...to.matched]
    .reverse()
    .find(({ meta }) => meta?.title);
  const nearestWithMeta = [...to.matched]
    .reverse()
    .find(({ meta }) => meta?.metaTags);

  document.title = [
    (nearestWithTitle?.meta?.title as any)?.(),
    SETTINGS.app.name,
  ]
    .filter(Boolean)
    .join(" - ");

  // Remove any stale meta tags from the document using the key attribute we set below.
  Array.from(document.querySelectorAll(`[${ROUTER_CONTROLLED_META}]`)).map(
    (el) => el?.parentNode?.removeChild(el)
  );

  // Turn the meta tag definitions into actual elements in the head.
  (nearestWithMeta?.meta?.metaTags as any[])
    ?.map((tagDef) => {
      const tag = document.createElement("meta");

      Object.entries(tagDef).forEach(([key, val]) => {
        const valResult = typeof val === "function" ? val() : val;
        if (valResult) {
          tag.setAttribute(key, valResult);
        }
      });

      // We use this to track which meta tags we create so we don't interfere with other ones.
      tag.setAttribute(ROUTER_CONTROLLED_META, "");

      return tag;
    })
    .forEach((tag) => document.head.appendChild(tag));
});

export default router;
